import { memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { MatchState, PatchSniperMarketDto, SniperMarket } from '../../types/sniper.types';
import {
  DEFAULT_SELL_ON_ANNULLED_GOAL,
  DEFAULT_SELL_ON_VAR,
  DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
  DEFAULT_VAR_REBUY_ENABLED,
  persistedLadderBands,
  gatePct,
  isArmed,
  ladderInvalidReason,
  normalizeDecimalInput,
  pctToGate,
  targetSummary,
  venueColor,
  venueLabel,
} from '../../utils/sniperArm';
import { SniperConfigPopover, type SniperConfigDraft } from './SniperConfigPopover';
import {
  NO_SIZE_HELP,
  NO_SIZE_LABEL,
  noSizeToInput,
  parseNoSizeInput,
} from '../shared/sniperNoSize';

/**
 * One Sniper market row: venue chip · label · counter/team/line · the per-row
 * editable params (size / ladder config / auto-NO ceiling + size / confidence
 * gate) and the sell switches, with a DRAFT + ✓ confirm (mirror of the opener
 * entry table — edits stay local until the operator commits, so a typo isn't
 * written mid-keystroke) · an enabled toggle (commits immediately) · a live
 * ARMED badge computed from the feed-derived `MatchState` (`count == floor(line)`).
 *
 * Prices live ONLY in the ladder config: a rung with no sell price simply does
 * not sell there, and the sell switches say whether a VAR or an annulled goal
 * gets us out at all.
 */
export const SniperMarketRow = memo(function SniperMarketRow({
  market,
  state,
  onPatch,
  onDelete,
  busy,
  selected,
  onToggleSelected,
  onDirtyChange,
}: {
  market: SniperMarket;
  /** Feed-derived live state; null until the event is active + streaming. */
  state: MatchState | null;
  onPatch: (patch: PatchSniperMarketDto) => void;
  onDelete: () => void;
  busy?: boolean;
  /** Whether this row is selected for a bulk action. */
  selected?: boolean;
  /** Toggle this row's selection. When omitted, no selection checkbox renders. */
  onToggleSelected?: () => void;
  /**
   * Report this row's uncommitted-edit state upward. Drafts live in THIS
   * component, so without lifting them the card's bulk Enable cannot know a
   * selected row has unsaved params and would arm it at the OLD values.
   */
  onDirtyChange?: (marketId: string, dirty: boolean) => void;
}) {
  // Draft strings — free text while editing, committed on ✓. The gate is edited
  // as an INTEGER PERCENT (1..100); the persisted `confidenceGate` stays 0..1.
  const [size, setSize] = useState(String(market.size));
  // The sell switches. A persisted `null` predates the field — its EFFECTIVE
  // value is the default the backend coerces to, so that is what the operator
  // sees + edits.
  const [sellOnVar, setSellOnVar] = useState(market.sellOnVar ?? DEFAULT_SELL_ON_VAR);
  const [sellOnAnnulledGoal, setSellOnAnnulledGoal] = useState(
    market.sellOnAnnulledGoal ?? DEFAULT_SELL_ON_ANNULLED_GOAL,
  );
  const [varRebuyEnabled, setVarRebuyEnabled] = useState(
    market.varRebuyEnabled ?? DEFAULT_VAR_REBUY_ENABLED,
  );
  // The ladder config — rungs, floor, VAR offset. Edited in a popover because a
  // ladder is a TABLE that no longer fits the row. The rungs are shown EXACTLY
  // as stored: a row left unconfigured (`[]`/`null`) reads as unconfigured and
  // blocks the ✓ until the operator sets real ones — the FE never substitutes a
  // default the server itself refuses to invent (see `persistedLadderBands`).
  const persistedConfig = useMemo<SniperConfigDraft>(
    () => ({
      ladderBands: persistedLadderBands(market.ladderBands),
      floorPrice: market.floorPrice,
      varPreventiveExitOffset:
        market.varPreventiveExitOffset ?? DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
    }),
    [market.ladderBands, market.floorPrice, market.varPreventiveExitOffset],
  );
  const [config, setConfig] = useState<SniperConfigDraft>(persistedConfig);
  // Auto-NO period-close tail. `autoNoEnabled`/`autoNoIncludeExtraTime` are
  // fail-safe-defaulted false at the schema level (never `null`), so unlike the
  // sell switches above they need no `?? DEFAULT` fallback.
  const [autoNoEnabled, setAutoNoEnabled] = useState(market.autoNoEnabled);
  const [autoNoIncludeExtraTime, setAutoNoIncludeExtraTime] = useState(
    market.autoNoIncludeExtraTime,
  );
  // `noMaxPrice` is shown EXACTLY as stored — blank when unset. The schema
  // defaults it to `null` and `createMarkets` writes `draft.noMaxPrice ?? null`,
  // so there is no backend default to mirror: seeding 0.99 here made a legacy
  // row look like it carried a ceiling and, because the `?? DEFAULT` comparison
  // also hid it from `dirty`, a Size-only ✓ silently persisted a 99¢ ceiling
  // nobody chose — live the moment anyone ticks Auto-NO.
  const [noMaxPrice, setNoMaxPrice] = useState(
    market.noMaxPrice == null ? '' : String(market.noMaxPrice),
  );
  // `noSize` is NULL-MEANINGFUL (blank = "inherit the main Size", a real valid
  // state resolved by the engine) — unlike every price field above, it must
  // NOT coerce a blank/absent value to a DEFAULT constant, or the draft could
  // never round-trip back to null (the exact bug the split-path-configs
  // Wait/Hedge fields hit). `noSizeToInput`/`parseNoSizeInput` are the shared
  // raw-preserving mapping (mirrors `counterStuckSeconds`).
  const [noSize, setNoSize] = useState(noSizeToInput(market.noSize));
  const [gate, setGate] = useState(String(gatePct(market.confidenceGate)));

  // Re-seed each draft from the persisted row INDEPENDENTLY — a single effect
  // over all of them would reset every draft whenever any ONE changed
  // server-side, discarding the other uncommitted edits.
  useEffect(() => {
    setSize(String(market.size));
  }, [market.size]);
  useEffect(() => {
    setConfig(persistedConfig);
  }, [persistedConfig]);
  useEffect(() => {
    setSellOnVar(market.sellOnVar ?? DEFAULT_SELL_ON_VAR);
  }, [market.sellOnVar]);
  useEffect(() => {
    setSellOnAnnulledGoal(market.sellOnAnnulledGoal ?? DEFAULT_SELL_ON_ANNULLED_GOAL);
  }, [market.sellOnAnnulledGoal]);
  useEffect(() => {
    setVarRebuyEnabled(market.varRebuyEnabled ?? DEFAULT_VAR_REBUY_ENABLED);
  }, [market.varRebuyEnabled]);
  useEffect(() => {
    setAutoNoEnabled(market.autoNoEnabled);
  }, [market.autoNoEnabled]);
  useEffect(() => {
    setAutoNoIncludeExtraTime(market.autoNoIncludeExtraTime);
  }, [market.autoNoIncludeExtraTime]);
  useEffect(() => {
    setNoMaxPrice(market.noMaxPrice == null ? '' : String(market.noMaxPrice));
  }, [market.noMaxPrice]);
  useEffect(() => {
    setNoSize(noSizeToInput(market.noSize));
  }, [market.noSize]);
  useEffect(() => {
    setGate(String(gatePct(market.confidenceGate)));
  }, [market.confidenceGate]);

  const parsed = useMemo(() => {
    const s = Number(size);
    // Blank = UNSET (`null` on the wire), exactly as stored. Only a non-blank
    // value is range-checked; the toggle decides whether one is required.
    const noMaxRaw = noMaxPrice.trim();
    const n = noMaxRaw === '' ? null : Number(noMaxRaw);
    // `noSize` is null-meaningful (blank = valid "inherit `size`"), so — unlike
    // every other numeric field here — its parse result can't collapse
    // "empty" and "invalid" onto the same `null` sentinel. Keep the resolved
    // value (only safe to read when `noSizeValid`) separate from validity.
    const parsedNoSize = parseNoSizeInput(noSize);
    // Percent → 0..1 gate; `null` for empty / non-numeric / out-of-range.
    const gateValue = gate.trim() === '' ? null : pctToGate(Number(gate));

    // The FIRST thing wrong with this row, as an operator-facing sentence. One
    // string drives the ✓ tooltip AND the enable switch, so the row can never
    // offer an action it has already said is impossible.
    const reason = (() => {
      // size >= 1 mirrors the backend DTO's `@Min(1)` (and the bulk bar) — a
      // fractional size only earns a 400.
      if (!(Number.isFinite(s) && s >= 1)) return 'size must be at least 1';
      // The auto-NO ceiling is a dollar price — (0,1], checked whenever one is
      // present so a bad value can never sit behind the toggle waiting to be
      // flipped on. It is REQUIRED only once the tail is enabled.
      if (n !== null && !(Number.isFinite(n) && n > 0 && n <= 1)) {
        return 'No $ must be between 0 and 1';
      }
      if (autoNoEnabled && n === null) return 'Auto-NO needs a No $ ceiling';
      if (!parsedNoSize.ok) return `No Size: ${parsedNoSize.error.toLowerCase()}`;
      if (gateValue === null) return 'Gate % must be between 1 and 100';
      // The ladder is the row's ONLY price config, so it is always validated.
      // The floor checked is the EFFECTIVE one — omitting it from the patch
      // leaves the stored value in place, and the server re-validates that
      // against the new rungs.
      const ladder = ladderInvalidReason(
        config.ladderBands,
        config.floorPrice ?? market.floorPrice,
        {
          varPreventiveExitOffset: config.varPreventiveExitOffset,
          floorRequired: sellOnVar || sellOnAnnulledGoal,
        },
      );
      if (ladder) return `ladder: ${ladder}`;
      return null;
    })();

    return {
      size: s,
      noMaxPrice: n,
      noSize: parsedNoSize.ok ? parsedNoSize.value : null,
      gate: gateValue,
      reason,
      valid: reason === null,
    };
  }, [
    size,
    config,
    market.floorPrice,
    sellOnVar,
    sellOnAnnulledGoal,
    autoNoEnabled,
    noMaxPrice,
    noSize,
    gate,
  ]);

  const ladderBandsDirty =
    JSON.stringify(config.ladderBands) !== JSON.stringify(persistedConfig.ladderBands);

  const configDirty =
    config.floorPrice !== persistedConfig.floorPrice ||
    config.varPreventiveExitOffset !== persistedConfig.varPreventiveExitOffset ||
    ladderBandsDirty;

  const dirty =
    parsed.size !== market.size ||
    configDirty ||
    sellOnVar !== (market.sellOnVar ?? DEFAULT_SELL_ON_VAR) ||
    sellOnAnnulledGoal !==
      (market.sellOnAnnulledGoal ?? DEFAULT_SELL_ON_ANNULLED_GOAL) ||
    varRebuyEnabled !== (market.varRebuyEnabled ?? DEFAULT_VAR_REBUY_ENABLED) ||
    autoNoEnabled !== market.autoNoEnabled ||
    autoNoIncludeExtraTime !== market.autoNoIncludeExtraTime ||
    parsed.noMaxPrice !== market.noMaxPrice ||
    parsed.noSize !== market.noSize ||
    Number(gate) !== gatePct(market.confidenceGate);

  const armed = state
    ? isArmed(market.counter, state, market.line, market.team, market.period)
    : false;

  // Only the OFF→ON flip is gated (see the switch below) — by uncommitted edits,
  // and by the row being invalid at all.
  const blockedArm = dirty && !market.enabled;
  const blockedInvalidArm = !market.enabled && !parsed.valid;

  useEffect(() => {
    onDirtyChange?.(market._id, dirty);
  }, [dirty, market._id, onDirtyChange]);
  // A row that unmounts (deleted, or filtered out of the card) must not leave a
  // stale dirty flag behind that would block a later bulk Enable forever.
  useEffect(
    () => () => {
      onDirtyChange?.(market._id, false);
    },
    [market._id, onDirtyChange],
  );

  const commit = () => {
    if (!dirty || !parsed.valid || parsed.gate === null) return;
    onPatch({
      size: parsed.size,
      // Sent ONLY when the operator actually changed the rungs. A size-only edit
      // must never write prices back — least of all onto a row the migration
      // left unconfigured, where the values on screen would be this editor's own
      // suggestion rather than anything anyone chose.
      ...(ladderBandsDirty ? { ladderBands: config.ladderBands } : {}),
      // The patch DTO has no way to express "unset", so an unset floor is simply
      // not sent — it is only reachable while nothing can sell.
      ...(config.floorPrice != null ? { floorPrice: config.floorPrice } : {}),
      // A cleared cell means "use the engine default" — which is this exact
      // number, so it round-trips without the DTO needing an "unset".
      varPreventiveExitOffset:
        config.varPreventiveExitOffset ?? DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
      sellOnVar,
      sellOnAnnulledGoal,
      varRebuyEnabled,
      autoNoEnabled,
      autoNoIncludeExtraTime,
      // Same rule as the rungs: sent only when it actually changed, so an
      // unrelated edit never stamps a ceiling onto a row that stores none.
      ...(parsed.noMaxPrice !== market.noMaxPrice
        ? { noMaxPrice: parsed.noMaxPrice }
        : {}),
      noSize: parsed.noSize,
      confidenceGate: parsed.gate,
    });
  };

  // Shared compact styling for the param inputs — tighter than the MUI `small`
  // default so more rows fit on screen.
  const numFieldSx = {
    '& .MuiInputBase-input': { py: 0.35, px: 0.75, fontSize: 12 },
    '& .MuiInputLabel-root': { fontSize: 10.5 },
    '& .MuiInputLabel-shrink': { fontSize: 11 },
  };

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      useFlexGap
      sx={{
        py: 0.4,
        px: 0.75,
        borderRadius: 1,
        border: 1,
        borderColor: armed ? '#ff5252' : 'divider',
        bgcolor: armed ? 'rgba(255,82,82,0.08)' : 'transparent',
        flexWrap: 'wrap',
      }}
    >
      {onToggleSelected && (
        // NOT disabled by `busy`: selection is local state with no write of its
        // own, so a single-row save must not freeze selection across the card.
        <Checkbox
          size="small"
          checked={!!selected}
          onChange={onToggleSelected}
          inputProps={{ 'aria-label': 'select market for bulk edit' }}
          sx={{ p: 0.25 }}
        />
      )}
      <Chip
        size="small"
        label={venueLabel(market.venue)}
        sx={{
          bgcolor: venueColor(market.venue),
          color: '#fff',
          fontWeight: 700,
          height: 18,
          fontSize: 11,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
      <Box sx={{ minWidth: 160, flex: 1 }}>
        <Typography variant="body2" sx={{ color: 'common.white', fontSize: 12.5, lineHeight: 1.25 }}>
          {market.marketLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, lineHeight: 1.2 }}>
          {targetSummary(market.counter, market.line, market.team, market.period)}
        </Typography>
      </Box>

      {/* ARMED badge — live, from the feed-derived state. */}
      {armed && (
        <Chip
          size="small"
          label="ARMED"
          sx={{
            bgcolor: '#ff5252',
            color: '#fff',
            fontWeight: 800,
            letterSpacing: 0.5,
            height: 18,
            fontSize: 10.5,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      )}

      <TextField
        size="small"
        label="Size"
        value={size}
        onChange={(e) => setSize(normalizeDecimalInput(e.target.value))}
        inputProps={{ inputMode: 'decimal', 'aria-label': 'sweep size' }}
        sx={{ width: 60, ...numFieldSx }}
      />
      <SniperConfigPopover
        value={config}
        onApply={setConfig}
        disabled={busy}
        floorRequired={sellOnVar || sellOnAnnulledGoal}
        // `PatchSniperMarketDto` has no way to express "unset", so a cleared
        // floor would be omitted and silently restored by the refetch.
        floorClearable={false}
      />
      <Tooltip title="Auto-NO period-close tail — worst per-contract price the NO buy may pay">
        <TextField
          size="small"
          label="No $"
          value={noMaxPrice}
          onChange={(e) => setNoMaxPrice(normalizeDecimalInput(e.target.value))}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'auto-no max price' }}
          sx={{ width: 60, ...numFieldSx }}
        />
      </Tooltip>
      <Tooltip title={NO_SIZE_HELP}>
        <TextField
          size="small"
          label={NO_SIZE_LABEL}
          placeholder="= Size"
          value={noSize}
          onChange={(e) => setNoSize(normalizeDecimalInput(e.target.value))}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'auto-no size' }}
          sx={{ width: 60, ...numFieldSx }}
        />
      </Tooltip>
      <RowToggle
        label="Sell VAR"
        tooltip="Sell on VAR — post the exit the moment a VAR review follows our goal. Off = ride the review out."
        ariaLabel="sell on var"
        checked={sellOnVar}
        onChange={setSellOnVar}
        disabled={busy}
      />
      <RowToggle
        label="Sell annul"
        tooltip="Sell on annulled goal — dump to the floor once the goal is annulled. Off = the operator unwinds by hand."
        ariaLabel="sell on annulled goal"
        checked={sellOnAnnulledGoal}
        onChange={setSellOnAnnulledGoal}
        disabled={busy}
      />
      <RowToggle
        label="Rebuy"
        tooltip="All-clear rebuy if the VAR confirms the goal"
        ariaLabel="var rebuy enabled"
        checked={varRebuyEnabled}
        onChange={setVarRebuyEnabled}
        disabled={busy}
      />
      <RowToggle
        label="Auto-NO"
        tooltip="Auto-NO period-close tail — buy the NO the instant the period ends without the Over crossing"
        ariaLabel="auto-no enabled"
        checked={autoNoEnabled}
        onChange={setAutoNoEnabled}
        disabled={busy}
      />
      <RowToggle
        label="ET"
        tooltip="Include extra time in the auto-NO tail (only meaningful for full / 2nd-half markets)"
        ariaLabel="auto-no include extra time"
        checked={autoNoIncludeExtraTime}
        onChange={setAutoNoIncludeExtraTime}
        disabled={busy || !autoNoEnabled || market.period === 'first_half'}
      />
      <TextField
        size="small"
        label="Gate %"
        value={gate}
        onChange={(e) => setGate(e.target.value.replace(/[^0-9]/g, ''))}
        inputProps={{
          inputMode: 'numeric',
          maxLength: 3,
          'aria-label': 'confidence gate percent',
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: 11 } }}>
              %
            </InputAdornment>
          ),
        }}
        sx={{ width: 78, ...numFieldSx }}
      />

      <Tooltip title={parsed.valid ? 'Save changes' : (parsed.reason ?? '')}>
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={commit}
            disabled={!dirty || !parsed.valid || busy}
            aria-label="confirm market params"
            sx={{ p: 0.5 }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {/* ARMING is blocked while this row has UNCOMMITTED param edits: the switch
          writes only {enabled}, so it would arm the market at the OLD size/gate
          while the inputs display the new ones. It is equally blocked while the
          row is INVALID — enabling runs the server's full validation, so an
          unconfigured market (the chip already reads "no ladder — configure")
          would 400, and offering the one action that arms real orders on a row
          we have just called untradeable is worse than the 400. Turning it OFF
          is never blocked — off can't make a market fire, and gating the safe
          direction would put friction on the one action an operator may need in
          a hurry. */}
      <Tooltip
        title={
          blockedArm
            ? 'Confirm (✓) or revert the edited params before enabling'
            : blockedInvalidArm
              ? `Cannot enable — ${parsed.reason}`
              : market.enabled
                ? 'Enabled'
                : 'Disabled (fail-safe OFF)'
        }
      >
        <span>
          <Switch
            size="small"
            checked={market.enabled}
            onChange={(e) => onPatch({ enabled: e.target.checked })}
            disabled={busy || blockedArm || blockedInvalidArm}
            inputProps={{ 'aria-label': 'enable market' }}
          />
        </span>
      </Tooltip>

      <Tooltip title="Delete market">
        <IconButton
          size="small"
          onClick={onDelete}
          disabled={busy}
          aria-label="delete market"
          sx={{ p: 0.5 }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
});

/**
 * A compact draft toggle for one of the row's booleans. Part of the row DRAFT
 * (like the numeric params) — it flips local state, marks the row dirty, and is
 * written only on ✓. A tiny white caption sits above the switch so the operator
 * can tell them apart in the dense row.
 */
function RowToggle({
  label,
  tooltip,
  ariaLabel,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  tooltip: string;
  ariaLabel: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Tooltip title={tooltip}>
      <Stack alignItems="center" spacing={0} sx={{ flexShrink: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: 'common.white', fontSize: 9, lineHeight: 1, whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
        <Switch
          size="small"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          inputProps={{ 'aria-label': ariaLabel }}
          sx={{ m: 0 }}
        />
      </Stack>
    </Tooltip>
  );
}
